import { useCallback } from "react"
import { useClient } from "urql"

import "../graphql/payFileGet.query"

import { PAY_FILE_PROCESSING_POLL_INTERVAL_MS, PAY_FILE_PROCESSING_POLL_TIMEOUT_MS } from "../constants"
import type { PayFileProcessingOutcome } from "../utils"

import { GetPayFileDocument, PayFileStatus } from "@/graphql/codegen/graphql"

export interface UseWaitForPayFileProcessingReturn {
  waitForProcessing: (payFileId: string) => Promise<PayFileProcessingOutcome | undefined>
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

export const useWaitForPayFileProcessing = (): UseWaitForPayFileProcessingReturn => {
  const client = useClient()

  // Resolves once the pay file leaves Processing, or with nothing after the timeout. A failed poll is
  // skipped rather than reported, since the next one can still succeed.
  const waitForProcessing = useCallback(
    async (payFileId: string): Promise<PayFileProcessingOutcome | undefined> => {
      const deadline = Date.now() + PAY_FILE_PROCESSING_POLL_TIMEOUT_MS

      while (Date.now() < deadline) {
        await wait(PAY_FILE_PROCESSING_POLL_INTERVAL_MS)

        const { data } = await client
          .query(GetPayFileDocument, { input: { payFileId } }, { requestPolicy: "network-only" })
          .toPromise()
        const output = data?.payFile.get

        if (output?.__typename === "PayFileGetSuccess" && output.payFile.status !== PayFileStatus.Processing) {
          return { status: output.payFile.status, latestLog: output.payFile.latestLog }
        }
      }

      return undefined
    },
    [client]
  )

  return { waitForProcessing }
}
